/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import {
  Icons,
  PLACEMENT,
  Tooltip,
  TOOLTIP_PLACEMENT,
} from '@web-stories-wp/design-system';
import { __ } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import { useConfig, useLayout, useStory } from '../../app';
import {
  RECORDER_HORIZONTAL_INSET,
  VideoModeLayer,
} from '../canvas/dynamicCropRecorderLayer';
import {
  formatRecorderTime,
  RECORDER_PLAYBACK_RATE_OPTIONS,
  RecordingActionGroup,
  RecordingCancelButton,
  RecordingControlButton,
  RecordingControlGroup,
  RecordingControls,
  RecordingControlsRow,
  RecordingDoneButton,
  RecordingFrameStepIcon,
  RecordingPlayButton,
  RecordingShell,
  RecordingSpeed,
  RecordingSpeedWrapper,
  RecordingStage,
  RecordingTime,
  RecordingVideo,
  RecordingVideoArea,
  recordingSpeedMenuStyles,
} from '../../elements/video/dynamicCropRecorderDisplay';
import { getDynamicCropGeometry } from '../../elements/video/dynamicCrop';
import useVideoTrim from './useVideoTrim';

const TRIM_FRAME_RATE = 30;

const TrimStage = styled(RecordingStage)`
  background: transparent;
`;

const TrimVideo = styled(RecordingVideo)`
  object-fit: cover;
`;

const TrimControlGroup = styled(RecordingControlGroup)`
  gap: 12px;
`;

const TrimLoopButton = styled(RecordingControlButton)`
  background: transparent;

  &[aria-pressed='true'] {
    color: ${({ theme }) => theme.colors.primary};
    background: transparent;
  }

  &:hover:not(:disabled),
  &:focus {
    background: transparent;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HiddenTimecodes = styled(RecordingTime)`
  opacity: 0;
`;

const Timeline = styled.div`
  position: relative;
  width: 100%;
  height: 24px;
  margin: 0 0 8px;
`;

const TimelineTrack = styled.div`
  position: absolute;
  top: 10px;
  right: 0;
  left: 0;
  height: 4px;
  overflow: hidden;
  border-radius: 8px;
  background: #4c5253;
`;

const TimelineProgress = styled.div`
  width: ${({ $progress }) => `${$progress * 100}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
`;

const Selection = styled.div`
  position: absolute;
  z-index: 2;
  top: 7px;
  left: ${({ $start }) => `${$start * 100}%`};
  width: ${({ $width }) => `${$width * 100}%`};
  height: 10px;
  border-top: 2px solid ${({ theme }) => theme.colors.standard.white};
  border-bottom: 2px solid ${({ theme }) => theme.colors.standard.white};
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
`;

const SelectionHandle = styled.span`
  position: absolute;
  top: -2px;
  ${({ $isEnd }) => ($isEnd ? 'right: -7px;' : 'left: -7px;')}
  width: 7px;
  height: 10px;
  border-radius: ${({ $isEnd }) => ($isEnd ? '0 4px 4px 0' : '4px 0 0 4px')};
  background: ${({ theme }) => theme.colors.standard.white};

  &::after {
    position: absolute;
    top: 3px;
    left: ${({ $isEnd }) => ($isEnd ? '1px' : '4px')};
    width: 2px;
    height: 4px;
    border-radius: 3px;
    background: ${({ theme }) => theme.colors.bg.primary};
    content: '';
  }
`;

const Playhead = styled.span`
  position: absolute;
  z-index: 3;
  top: 0;
  left: ${({ $progress }) => `${$progress * 100}%`};
  width: 24px;
  height: 24px;
  border: 2px solid ${({ theme }) => theme.colors.bg.primary};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.fg.primary};
  pointer-events: none;
  transform: translateX(-50%);
`;

const PlayheadInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 24px;
  margin: 0;
  opacity: 0;
  cursor: pointer;
`;

function FixedTrimTimeline({
  currentOffset,
  endOffset,
  maxOffset,
  onCurrentOffsetChange,
  onStartOffsetChange,
  startOffset,
}) {
  const timelineRef = useRef(null);
  const dragState = useRef(null);

  if (!maxOffset) {
    return null;
  }

  const maxStart = Math.max(0, maxOffset - (endOffset - startOffset));
  const selectionDuration = Math.max(0, endOffset - startOffset);
  const startRatio = startOffset / maxOffset;
  const selectionRatio = selectionDuration / maxOffset;
  const currentRatio = Math.min(Math.max(currentOffset / maxOffset, 0), 1);

  const moveSelection = (requestedStart) => {
    onStartOffsetChange(Math.min(Math.max(requestedStart, 0), maxStart));
  };

  const handleSelectionPointerDown = (event) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    dragState.current = {
      clientX: event.clientX,
      startOffset,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleSelectionPointerMove = (event) => {
    const timelineWidth = timelineRef.current?.getBoundingClientRect().width;
    if (!dragState.current || !timelineWidth) {
      return;
    }
    const delta =
      ((event.clientX - dragState.current.clientX) / timelineWidth) * maxOffset;
    moveSelection(dragState.current.startOffset + delta);
  };

  const handleSelectionPointerUp = (event) => {
    dragState.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleSelectionKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    moveSelection(startOffset + direction * (event.shiftKey ? 1000 : 100));
  };

  return (
    <Timeline ref={timelineRef}>
      <TimelineTrack>
        <TimelineProgress $progress={currentRatio} />
      </TimelineTrack>
      <PlayheadInput
        type="range"
        aria-label={__('Video playhead', 'web-stories')}
        min={0}
        max={maxOffset}
        step={1000 / TRIM_FRAME_RATE}
        value={currentOffset}
        onChange={(event) => onCurrentOffsetChange(Number(event.target.value))}
      />
      <Selection
        role="slider"
        tabIndex={0}
        aria-label={__('Thumbnail range', 'web-stories')}
        aria-valuemin={0}
        aria-valuemax={maxStart}
        aria-valuenow={startOffset}
        $start={startRatio}
        $width={selectionRatio}
        onKeyDown={handleSelectionKeyDown}
        onPointerDown={handleSelectionPointerDown}
        onPointerMove={handleSelectionPointerMove}
        onPointerUp={handleSelectionPointerUp}
        onPointerCancel={handleSelectionPointerUp}
      >
        <SelectionHandle />
        <SelectionHandle $isEnd />
      </Selection>
      <Playhead $progress={currentRatio} />
    </Timeline>
  );
}

FixedTrimTimeline.propTypes = {
  currentOffset: PropTypes.number,
  endOffset: PropTypes.number.isRequired,
  maxOffset: PropTypes.number.isRequired,
  onCurrentOffsetChange: PropTypes.func.isRequired,
  onStartOffsetChange: PropTypes.func.isRequired,
  startOffset: PropTypes.number.isRequired,
};

function TrimTransportButton({
  children,
  isLoopButton = false,
  isPlayButton = false,
  label,
  ...buttonProps
}) {
  let Control = RecordingControlButton;
  if (isPlayButton) {
    Control = RecordingPlayButton;
  } else if (isLoopButton) {
    Control = TrimLoopButton;
  }

  return (
    <Tooltip hasTail isDelayed placement={TOOLTIP_PLACEMENT.TOP} title={label}>
      <Control aria-label={label} title={label} {...buttonProps}>
        {children}
      </Control>
    </Tooltip>
  );
}

TrimTransportButton.propTypes = {
  children: PropTypes.node.isRequired,
  isLoopButton: PropTypes.bool,
  isPlayButton: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

function VideoTrimLayer() {
  const { sourceUrl: thumbnailSourceUrl, aspectRatio: thumbnailAspectRatio } =
    useConfig(({ animatedThumbnail }) => animatedThumbnail || {});
  const {
    cancelTrim,
    currentTime,
    endOffset,
    isAnimatedThumbnail,
    isLooping,
    isTrimMode,
    maxOffset,
    performTrim,
    setStartOffset,
    setIsLooping,
    setVideoNode,
    startOffset,
  } = useVideoTrim(
    ({
      state: {
        currentTime,
        endOffset,
        isAnimatedThumbnail,
        isLooping,
        isTrimMode,
        maxOffset,
        startOffset,
      },
      actions: {
        cancelTrim,
        performTrim,
        setIsLooping,
        setStartOffset,
        setVideoNode,
      },
    }) => ({
      cancelTrim,
      currentTime,
      endOffset,
      isAnimatedThumbnail,
      isLooping,
      isTrimMode,
      maxOffset,
      performTrim,
      setIsLooping,
      setStartOffset,
      setVideoNode,
      startOffset,
    })
  );
  const { selectedElement } = useStory(({ state: { selectedElements } }) => ({
    selectedElement: selectedElements.length === 1 ? selectedElements[0] : null,
  }));
  const { viewportHeight, viewportWidth } = useLayout(
    ({ state: { viewportHeight, viewportWidth } }) => ({
      viewportHeight,
      viewportWidth,
    })
  );
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playheadOffset, setPlayheadOffset] = useState(0);

  const isActive = Boolean(
    isAnimatedThumbnail &&
      isTrimMode &&
      selectedElement?.type === 'video' &&
      thumbnailSourceUrl &&
      selectedElement?.resource?.src === thumbnailSourceUrl
  );
  const isTrimReady = Boolean(
    Number.isFinite(startOffset) &&
      Number.isFinite(endOffset) &&
      Number.isFinite(maxOffset) &&
      maxOffset > 0
  );
  const setRef = useCallback(
    (node) => {
      videoRef.current = node;
      setVideoNode(node);
    },
    [setVideoNode]
  );

  useEffect(() => {
    const node = videoRef.current;
    if (!node) {
      return;
    }
    node.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (!isActive) {
      setIsPlaying(false);
      setIsLooping(true);
      setPlaybackRate(1);
    }
  }, [isActive, setIsLooping]);

  useEffect(() => {
    if (Number.isFinite(currentTime)) {
      setPlayheadOffset(currentTime);
    }
  }, [currentTime]);

  const seekTo = useCallback(
    (nextOffset) => {
      const node = videoRef.current;
      if (!node || !Number.isFinite(maxOffset)) {
        return;
      }
      const clampedOffset = Math.min(Math.max(nextOffset, 0), maxOffset);
      node.currentTime = clampedOffset / 1000;
      setPlayheadOffset(clampedOffset);
    },
    [maxOffset]
  );

  const pauseVideo = useCallback(() => {
    const node = videoRef.current;
    if (!node) {
      return null;
    }

    if (!node.paused) {
      node.pause();
    }
    setIsPlaying(false);
    return node;
  }, []);

  const pauseAndSeekTo = useCallback(
    (nextOffset) => {
      if (!pauseVideo()) {
        return;
      }
      seekTo(nextOffset);
    },
    [pauseVideo, seekTo]
  );

  const togglePlayback = useCallback(() => {
    const node = videoRef.current;
    if (!node || !isTrimReady) {
      return;
    }

    if (!node.paused) {
      node.pause();
      return;
    }

    const offset = node.currentTime * 1000;
    if (isLooping && (offset < startOffset || offset >= endOffset)) {
      seekTo(startOffset);
    } else if (!isLooping && offset >= maxOffset) {
      seekTo(0);
    }
    node.play()?.catch(() => setIsPlaying(false));
  }, [endOffset, isLooping, isTrimReady, maxOffset, seekTo, startOffset]);

  const toggleSelectionLoop = useCallback(() => {
    const nextIsLooping = !isLooping;
    const nodeOffset = videoRef.current?.currentTime * 1000;
    if (
      nextIsLooping &&
      Number.isFinite(nodeOffset) &&
      (nodeOffset < startOffset || nodeOffset >= endOffset)
    ) {
      pauseAndSeekTo(startOffset);
    }
    setIsLooping(nextIsLooping);
  }, [endOffset, isLooping, pauseAndSeekTo, setIsLooping, startOffset]);

  const handleFrameStep = useCallback(
    (direction) => {
      const node = pauseVideo();
      if (!isTrimReady || !node) {
        return;
      }
      const nodeOffset = Number.isFinite(node.currentTime)
        ? node.currentTime * 1000
        : playheadOffset;
      seekTo(nodeOffset + (1000 / TRIM_FRAME_RATE) * direction);
    },
    [isTrimReady, pauseVideo, playheadOffset, seekTo]
  );

  const updateSelectionStart = useCallback(
    (requestedStart, shouldSyncPlayhead = false) => {
      if (!isTrimReady) {
        return;
      }

      const selectionDuration = endOffset - startOffset;
      const nextStart = Math.min(
        Math.max(requestedStart, 0),
        maxOffset - selectionDuration
      );
      pauseVideo();
      setStartOffset(nextStart);
      if (shouldSyncPlayhead) {
        seekTo(nextStart);
      }
    },
    [
      endOffset,
      isTrimReady,
      maxOffset,
      pauseVideo,
      seekTo,
      setStartOffset,
      startOffset,
    ]
  );

  const moveSelectionFromTimeline = useCallback(
    (nextStart) => updateSelectionStart(nextStart, isLooping),
    [isLooping, updateSelectionStart]
  );

  const setTrimStartAtPlayhead = useCallback(() => {
    const nodeOffset = videoRef.current?.currentTime * 1000;
    if (Number.isFinite(nodeOffset)) {
      updateSelectionStart(nodeOffset);
    }
  }, [updateSelectionStart]);

  const setTrimEndAtPlayhead = useCallback(() => {
    const nodeOffset = videoRef.current?.currentTime * 1000;
    if (Number.isFinite(nodeOffset)) {
      updateSelectionStart(nodeOffset - (endOffset - startOffset));
    }
  }, [endOffset, startOffset, updateSelectionStart]);

  const handlePlaybackRateChange = useCallback((_, value) => {
    setPlaybackRate(Number(value));
  }, []);

  const sourceAspectRatio = useMemo(() => {
    const width = selectedElement?.resource?.width || selectedElement?.width;
    const height = selectedElement?.resource?.height || selectedElement?.height;
    return getDynamicCropGeometry(null, width, height).sourceAspectRatio;
  }, [selectedElement]);
  const previewAspectRatio =
    Number.isFinite(thumbnailAspectRatio) && thumbnailAspectRatio > 0
      ? thumbnailAspectRatio
      : sourceAspectRatio;

  if (!isActive) {
    return null;
  }

  let playbackLabel = isPlaying
    ? __('Pause video', 'web-stories')
    : __('Play video', 'web-stories');
  if (isLooping) {
    playbackLabel = isPlaying
      ? __('Pause thumbnail', 'web-stories')
      : __('Play thumbnail', 'web-stories');
  }

  const availableWidth = Math.max(
    viewportWidth - RECORDER_HORIZONTAL_INSET * 2,
    0
  );
  const shouldFillWidth =
    !viewportHeight || previewAspectRatio >= availableWidth / viewportHeight;
  const resource = selectedElement.resource;
  const videoSourceKey = `${resource.mimeType || ''}:${resource.src}`;

  return (
    <VideoModeLayer
      ariaLabel={__('Trim video layer', 'web-stories')}
      testId="videoTrimLayer"
      overlayTestId="videoTrimOverlay"
      isTransparent
    >
      <RecordingShell>
        <RecordingVideoArea>
          <TrimStage
            $fillWidth={shouldFillWidth}
            $sourceAspectRatio={previewAspectRatio}
          >
            <TrimVideo
              key={videoSourceKey}
              ref={setRef}
              playsInline
              preload="auto"
              poster={selectedElement.poster || resource.poster}
              muted
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onTimeUpdate={(event) =>
                setPlayheadOffset(event.currentTarget.currentTime * 1000)
              }
            >
              <source src={resource.src} type={resource.mimeType} />
              <source src={resource.src} />
            </TrimVideo>
            <RecordingActionGroup>
              <RecordingCancelButton onClick={cancelTrim}>
                {__('Cancel', 'web-stories')}
              </RecordingCancelButton>
              <RecordingDoneButton
                onClick={performTrim}
                disabled={!isTrimReady}
              >
                {__('Done', 'web-stories')}
              </RecordingDoneButton>
            </RecordingActionGroup>
          </TrimStage>
        </RecordingVideoArea>
        <RecordingControls
          aria-label={__('Trim video controls', 'web-stories')}
        >
          {isTrimReady ? (
            <FixedTrimTimeline
              currentOffset={playheadOffset}
              endOffset={endOffset}
              maxOffset={maxOffset}
              startOffset={startOffset}
              onCurrentOffsetChange={pauseAndSeekTo}
              onStartOffsetChange={moveSelectionFromTimeline}
            />
          ) : (
            <Timeline aria-hidden="true">
              <TimelineTrack />
            </Timeline>
          )}
          <RecordingControlsRow>
            <HiddenTimecodes aria-hidden="true">
              <span>{formatRecorderTime(playheadOffset / 1000)}</span>
              <span>{formatRecorderTime(maxOffset / 1000)}</span>
            </HiddenTimecodes>
            <TrimControlGroup>
              <TrimTransportButton
                label={__('Go to thumbnail start', 'web-stories')}
                onClick={() => pauseAndSeekTo(startOffset)}
                disabled={!isTrimReady}
              >
                <Icons.TrimGoToStart aria-hidden="true" />
              </TrimTransportButton>
              <TrimTransportButton
                label={__('Start thumbnail here', 'web-stories')}
                onClick={setTrimStartAtPlayhead}
                disabled={!isTrimReady}
              >
                <Icons.TrimSetStart aria-hidden="true" />
              </TrimTransportButton>
              <TrimTransportButton
                label={__('Previous frame', 'web-stories')}
                onClick={() => handleFrameStep(-1)}
                disabled={!isTrimReady}
              >
                <RecordingFrameStepIcon
                  $direction="previous"
                  aria-hidden="true"
                >
                  <Icons.ArrowLeft />
                </RecordingFrameStepIcon>
              </TrimTransportButton>
              <TrimTransportButton
                isPlayButton
                label={playbackLabel}
                onClick={togglePlayback}
                disabled={!isTrimReady}
              >
                {isPlaying ? (
                  <Icons.PauseOutline aria-hidden="true" />
                ) : (
                  <Icons.PlayOutline aria-hidden="true" />
                )}
              </TrimTransportButton>
              <TrimTransportButton
                label={__('Next frame', 'web-stories')}
                onClick={() => handleFrameStep(1)}
                disabled={!isTrimReady}
              >
                <RecordingFrameStepIcon $direction="next" aria-hidden="true">
                  <Icons.ArrowRight />
                </RecordingFrameStepIcon>
              </TrimTransportButton>
              <TrimTransportButton
                label={__('End thumbnail here', 'web-stories')}
                onClick={setTrimEndAtPlayhead}
                disabled={!isTrimReady}
              >
                <Icons.TrimSetEnd aria-hidden="true" />
              </TrimTransportButton>
              <TrimTransportButton
                label={__('Go to thumbnail end', 'web-stories')}
                onClick={() => pauseAndSeekTo(endOffset)}
                disabled={!isTrimReady}
              >
                <Icons.TrimGoToEnd aria-hidden="true" />
              </TrimTransportButton>
            </TrimControlGroup>
            <RecordingSpeedWrapper>
              <TrimTransportButton
                isLoopButton
                label={
                  isLooping
                    ? __('Disable thumbnail loop', 'web-stories')
                    : __('Loop thumbnail', 'web-stories')
                }
                aria-pressed={isLooping}
                onClick={toggleSelectionLoop}
                disabled={!isTrimReady}
              >
                <Icons.ReplayCircleFilled aria-hidden="true" />
              </TrimTransportButton>
              <RecordingSpeed
                ariaLabel={__('Playback speed', 'web-stories')}
                dropDownLabel=""
                placement={PLACEMENT.TOP_END}
                popupFillWidth={false}
                popupZIndex={30}
                menuStylesOverride={recordingSpeedMenuStyles}
                options={RECORDER_PLAYBACK_RATE_OPTIONS}
                selectedValue={playbackRate}
                onMenuItemClick={handlePlaybackRateChange}
              />
            </RecordingSpeedWrapper>
          </RecordingControlsRow>
        </RecordingControls>
      </RecordingShell>
    </VideoModeLayer>
  );
}

export default VideoTrimLayer;
